class ClendoNativeSearch extends HTMLElement {
  connectedCallback() {
    if (this.moving) return;
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.input = this.querySelector('input[type="search"]');
    this.form = this.querySelector('form');
    this.panel = this.querySelector('.cl-search-panel');
    this.results = this.querySelector('[data-search-results]');
    this.status = this.querySelector('[data-search-status]');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.allLink = this.querySelector('[data-search-all]');
    this.cache ||= new Map();
    this.sequence = 0;
    this.mobile = matchMedia('(max-width:749px)');
    this.dialog = document.getElementById('clendo-search-dialog');
    this.input.addEventListener('input', event => {
      this.cancelRequest();
      this.clearButton.hidden = !this.input.value;
      if (event.isComposing) return;
      this.schedule();
    }, { signal });
    this.input.addEventListener('compositionend', () => this.schedule(), { signal });
    this.input.addEventListener('focus', () => {
      if (!this.suppressFocus && this.input.value.trim().length >= 2) this.schedule();
    }, { signal });
    this.clearButton.addEventListener('click', () => {
      this.cancelRequest();
      this.input.value = '';
      this.clearButton.hidden = true;
      this.hide();
      this.results.replaceChildren();
      this.status.textContent = this.dataset.hint;
      this.input.focus();
    }, { signal });
    this.querySelector('[data-search-close]').addEventListener('click', () => this.dismiss(), { signal });
    this.querySelector('[data-search-close-mobile]').addEventListener('click', () => this.closeMobile(), { signal });
    this.addEventListener('keydown', event => this.onKey(event), { signal });
    this.addEventListener('focusout', event => {
      if (!this.dialog?.open && !this.contains(event.relatedTarget)) { this.cancelRequest(); this.hide(); }
    }, { signal });
    document.addEventListener('pointerdown', event => {
      if (!this.dialog?.open && !this.contains(event.target)) { this.cancelRequest(); this.hide(); }
    }, { signal });
    document.addEventListener('click', event => {
      const trigger = event.target.closest('[data-native-search-trigger]');
      if (!trigger) return;
      event.preventDefault();
      if (this.mobile.matches) this.openMobile(trigger);
      else this.input.focus();
    }, { signal });
    this.bindDialog();
    this.mobile.addEventListener('change', () => this.closeMobile(), { signal });
    this.form.addEventListener('submit', () => this.cancelRequest(), { signal });
    this.clearButton.hidden = !this.input.value;
  }

  disconnectedCallback() {
    if (this.moving) return;
    this.cancelRequest();
    this.controller?.abort();
  }

  cancelRequest() {
    clearTimeout(this.timer);
    clearTimeout(this.timeout);
    this.request?.abort();
    this.sequence++;
    this.results?.removeAttribute('aria-busy');
  }

  schedule() {
    clearTimeout(this.timer);
    const query = this.input.value.trim();
    if (query.length < 2) { this.hide(); return; }
    this.timer = setTimeout(() => this.search(query), 180);
  }

  async search(query) {
    this.cancelRequest();
    const sequence = this.sequence;
    const target = new URL(this.form.action);
    target.searchParams.set('q', query);
    target.searchParams.set('options[prefix]', 'last');
    this.allLink.href = target.href;
    if (this.cache.has(query)) { this.render(this.cache.get(query)); return; }
    this.status.textContent = this.dataset.loading;
    this.results.setAttribute('aria-busy', 'true');
    this.request = new AbortController();
    this.timeout = setTimeout(() => this.request.abort(), 8000);
    const url = new URL(this.dataset.endpoint, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('section_id', 'clendo-predictive-search');
    url.searchParams.set('resources[type]', 'product,collection,page,article,query');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    try {
      const response = await fetch(url, { signal: this.request.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Search unavailable');
      const html = await response.text();
      if (sequence !== this.sequence || query !== this.input.value.trim()) return;
      const fragment = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-native-search-response]');
      if (!fragment) throw new Error('Search response missing');
      if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
      this.cache.set(query, fragment.outerHTML);
      this.render(fragment.outerHTML);
    } catch (error) {
      if (sequence !== this.sequence) return;
      const message = document.createElement('p');
      message.className = 'cl-search-empty';
      message.textContent = this.dataset.error;
      this.results.replaceChildren(message);
      this.status.textContent = this.dataset.error;
      this.show();
    } finally {
      if (sequence === this.sequence) {
        clearTimeout(this.timeout);
        this.results.removeAttribute('aria-busy');
      }
    }
  }

  render(html) {
    this.results.innerHTML = html;
    this.status.textContent = this.results.firstElementChild?.dataset.announcement || '';
    this.show();
  }

  show() { this.panel.hidden = false; this.input.setAttribute('aria-expanded', 'true'); }
  hide() { this.panel.hidden = true; this.input.setAttribute('aria-expanded', 'false'); }
  dismiss() {
    this.cancelRequest();
    this.hide();
    this.suppressFocus = true;
    this.input.focus();
    this.suppressFocus = false;
  }

  onKey(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (this.dialog?.open) this.closeMobile();
      else this.dismiss();
      return;
    }
    if (this.panel.hidden || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    const options = [...this.results.querySelectorAll('[data-search-option],.cs-crosssell-title'), this.allLink];
    const index = options.indexOf(document.activeElement);
    if (event.target !== this.input && index < 0) return;
    event.preventDefault();
    if (event.key === 'ArrowUp' && index === 0) { this.input.focus(); return; }
    const next = index < 0 ? (event.key === 'ArrowDown' ? 0 : options.length - 1) : (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
    options[next]?.focus();
  }

  bindDialog() {
    this.dialog = document.getElementById('clendo-search-dialog');
    if (!this.dialog || this.boundDialog === this.dialog) return;
    this.boundDialog = this.dialog;
    const signal = this.controller.signal;
    this.dialog.addEventListener('close', () => this.restoreDesktop(), { signal });
    this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.closeMobile(); }, { signal });
  }

  openMobile(trigger) {
    this.bindDialog();
    if (!this.dialog || this.dialog.open) return;
    this.trigger = trigger;
    this.home = document.createComment('native-search');
    this.before(this.home);
    this.moving = true;
    this.dialog.append(this);
    this.moving = false;
    this.dialog.showModal();
    document.documentElement.classList.add('cl-search-modal-open');
    this.trigger?.setAttribute('aria-expanded', 'true');
    this.status.textContent = this.dataset.hint;
    this.input.focus();
  }

  closeMobile() { if (this.dialog?.open) this.dialog.close(); }
  restoreDesktop() {
    this.cancelRequest();
    this.hide();
    this.moving = true;
    this.home?.replaceWith(this);
    this.moving = false;
    document.documentElement.classList.remove('cl-search-modal-open');
    this.trigger?.setAttribute('aria-expanded', 'false');
    if (this.mobile.matches) this.trigger?.focus();
  }
}
if (!customElements.get('clendo-native-search')) customElements.define('clendo-native-search', ClendoNativeSearch);
